import { TZDate, tzdate } from '@repo/tzdate';
import { groupBy, mapValues, sum } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { CartItem, PackageTicketOptionWithQuantity, ProductInstance } from '@repo/types';
import { useAvailablePackageProducts } from '@repo/widget-utils/services/hooks/product';
import TourSelectionContainer from './subcomponents/TourSelectionContainer';
import { cartAtom } from 'src/state/cart/cartAtom';
import { createAddProductsToCartEvent } from 'src/state/cart/cart.reducer';
import { getPackageTicketOptionsWithTours } from '@repo/widget-utils/packages/getPackageTicketOptionsWithTours';
import { useTheme, Stack, Typography } from '@mui/material';
import { PackageSummary } from './PackageSummary';
import { useLocale } from '@repo/i18n';
import {
    CustomizationsContext,
    useCustomizations,
} from 'src/components/utils/theme/customizations';
import {
    validatePackageAvailabilities,
    validatePackageAvailability,
} from '@repo/widget-utils/packages/validation';

// Custom hook to manage package selection logic
function usePackageSelection(item: CartItem) {
    const { t } = useLocale();
    const pkg = item.pkg?.pkg ?? null;
    const { data: unfilteredAvailabilities } = useAvailablePackageProducts(
        pkg,
        item.pkg?.date ?? TZDate.now(),
    );

    const numberOfTravellers = useMemo(() => {
        return sum(item.ticketOptions.map((q) => q.quantity));
    }, [item.ticketOptions]);

    // Filter availabilities based on validation
    // This will return only those availabilities that are valid for the number of travellers
    const availabilities = useMemo(() => {
        if (!unfilteredAvailabilities) return [];
        return unfilteredAvailabilities.filter(
            (availability) =>
                validatePackageAvailability(availability, numberOfTravellers, t).length === 0,
        );
    }, [unfilteredAvailabilities, numberOfTravellers, t]);

    // Derive selectedAvailabilities from props or from getFirstAvailableProducts()
    const selectedAvailabilities = useMemo(() => {
        if (item.products.length > 0) return item.products;
        if (availabilities.length === 0) return [];
        return getFirstAvailableProducts(item, availabilities);
    }, [item, availabilities]);

    // Derive ticket options with tours
    const ticketOptionsWithTours = useMemo(
        () =>
            getPackageTicketOptionsWithTours(
                item.ticketOptions as PackageTicketOptionWithQuantity[],
                availabilities,
            ),
        [item.ticketOptions, availabilities],
    );

    // Derive ticket options with select Tours - used in summary component
    const ticketOptionsWithSelectedTours = useMemo(
        () =>
            getPackageTicketOptionsWithTours(
                item.ticketOptions as PackageTicketOptionWithQuantity[],
                selectedAvailabilities,
            ),
        [item.ticketOptions, selectedAvailabilities],
    );

    const onSelectAvailabilities = useCallback(
        async (selected: ProductInstance[]) => {
            if (!item.pkg) return;
            await cartAtom.update(
                createAddProductsToCartEvent(
                    selected,
                    item.ticketOptions,
                    true, // Packages is not supported by membership api, so this item will disable payment plans
                    item.pkg,
                ),
            );
        },
        [item],
    );

    // Update cart on initial render if we have availabilities but no selected products
    useEffect(() => {
        if (availabilities.length > 0 && item.products.length === 0) {
            const initialProducts = getFirstAvailableProducts(item, availabilities);
            onSelectAvailabilities(initialProducts);
        }
    }, [availabilities, item, onSelectAvailabilities]);

    const validationErrors = useMemo(() => {
        return validatePackageAvailabilities(selectedAvailabilities, numberOfTravellers, t);
    }, [selectedAvailabilities, numberOfTravellers, t]);

    return {
        availabilities,
        selectedAvailabilities,
        ticketOptionsWithTours,
        ticketOptionsWithSelectedTours,
        onSelectAvailabilities,
        validationErrors,
    };
}

export default function TourSelection({
    item,
    initiallyExpanded,
}: {
    item: CartItem;
    initiallyExpanded?: boolean;
}) {
    const { t } = useLocale();
    const customizations = useCustomizations();
    const theme = useTheme();
    const {
        availabilities,
        selectedAvailabilities,
        ticketOptionsWithTours,
        ticketOptionsWithSelectedTours,
        onSelectAvailabilities,
        validationErrors,
    } = usePackageSelection(item);

    return (
        <CustomizationsContext.Provider
            value={{
                ...customizations,
                productCardColor: theme.palette.grey[50],
                productCardTextColor: theme.palette.common.black,
            }}
        >
            <Stack gap={2}>
                <Stack gap={1}>
                    <Typography
                        variant="h4"
                        fontSize={20}
                        fontWeight={700}
                        lineHeight={1}
                        m={0}
                        pt={2}
                    >
                        {t.change_selected_products}
                    </Typography>
                    <TourSelectionContainer
                        products={availabilities}
                        selectedAvailabilities={selectedAvailabilities}
                        setSelectedAvailabilities={onSelectAvailabilities}
                        ticketOptionsWithTours={ticketOptionsWithTours}
                        initiallyExpanded={initiallyExpanded}
                        validationErrors={validationErrors}
                    />
                </Stack>
                <Typography
                    variant="h4"
                    fontSize={20}
                    fontWeight={700}
                    lineHeight={1}
                    m={0}
                    pt={1}
                    mb={-1}
                >
                    {t.summary}
                </Typography>
                <PackageSummary
                    ticketOptionsWithTours={ticketOptionsWithSelectedTours}
                    validationErrors={validationErrors}
                />
            </Stack>
        </CustomizationsContext.Provider>
    );
}

export function getFirstAvailableProducts(
    item: CartItem,
    products: ProductInstance[],
): ProductInstance[] {
    if (!item.pkg) return [];
    const { pkg } = item.pkg;
    const packageProductsByTicketOptionId = mapValues(
        groupBy(pkg.products, (p) => p.pkgTicketOptionId),
        (ps) =>
            products.reduce((acc: ProductInstance[], cur) => {
                const newProducts = ps
                    .filter(
                        (p) =>
                            p.id === cur.product?.id &&
                            p.pkgProductId === cur.product?.pkgProductId,
                    )
                    .map((p) => ({ ...cur, product: p }));
                acc.push(...newProducts);
                return acc;
            }, []),
    );

    const sorted = mapValues(packageProductsByTicketOptionId, (ps) =>
        ps.sort((a, b) =>
            tzdate(a.start).isSame(tzdate(b.start))
                ? 0
                : tzdate(a.start).isBefore(tzdate(b.start))
                  ? -1
                  : 1,
        ),
    );

    const prods = mapValues(sorted, (p) => {
        return p.reduce((acc, cur) => {
            if (
                !!cur.product?.pkgProductId &&
                !acc.find((x) => x.product?.pkgProductId === cur.product?.pkgProductId) &&
                (item.ticketOptions.find((to) => to.id === cur.product?.pkgTicketOptionId)
                    ?.quantity ?? 0) > 0
            ) {
                acc.push(cur);
            }
            return acc;
        }, [] as ProductInstance[]);
    });

    return Object.values(prods).flat() as ProductInstance[];
}
